// Dossier Organisation (AGENT-795, lot R3 de design/os-reorganisation-ecrans) :
// Portes machine, Membres & annuaire, Usage & facturation, Droits, Coffre &
// événements, et « Mes accès » côté Run. Monté sous la clé `org`.
// L'espagnol retombe sur le français (fallbackLng) tant qu'il n'est pas traduit.
export const orgResources = {
  fr: {
    common: {
      identityUnresolved: 'Identité non résolue',
      loading: 'Chargement…',
      refresh: 'Actualiser',
      revoke: 'Révoquer',
      remove: 'Retirer',
      cancel: 'Annuler',
      never: 'jamais',
      notProvided: 'non renseigné',
      forbidden: 'Votre rôle ne permet pas de consulter cette page.',
      selectOrganization: 'Sélectionnez une organisation.',
      comingSoon: 'À venir',
      statusLabel: 'État',
      actions: 'Actions',
      status: {
        active: 'Actif',
        revoked: 'Révoqué',
        expired: 'Expiré',
        pending: 'En attente',
        unknown: 'État inconnu',
      },
    },
    gates: {
      title: 'Portes machine',
      description:
        'Les accès sans session humaine : agents de code, assistants branchés, nœuds, mandats d’approbation et mandats de nœud qui visent l’organisation.',
      tabs: {
        codeAgents: 'Agents de code',
        assistants: 'Assistants',
        nodes: 'Nœuds',
        mandates: 'Mandats d’approbation',
        nodeMandates: 'Mandats de nœud',
      },
    },
    assistants: {
      title: 'Assistants branchés',
      onOne:
        'Le branchement d’un assistant (URL MCP et jeton) est disponible sur One. Dans l’OS, il arrive avec le lot 2 de la porte MCP.',
      noList:
        'Aucune route de l’instance ne liste encore les jetons d’assistant hors du poste local.',
    },
    nodes: {
      ownerScope:
        'Le control plane ne liste que les nœuds liés à votre compte : il ne connaît pas les nœuds des autres membres.',
      nodesTitle: 'Nœuds liés',
      nodesEmpty: 'Aucun nœud lié à votre compte.',
      mandatesTitle: 'Mandats de nœud',
      mandatesEmpty: 'Aucun mandat de nœud.',
      node: 'Nœud',
      algorithm: 'Algorithme',
      pair: 'Paire',
      capability: 'Capacité',
      kind: 'Type',
      issuedAt: 'Émis le',
      revokedAt: 'Révoqué le',
      hankoRequired:
        'Nécessite une session Hanko : le control plane des nœuds n’accepte pas une session locale.',
      disabled: 'Le control plane des nœuds n’est pas activé sur cette instance.',
      error: 'Impossible de charger les nœuds.',
      revokeNodeTitle: 'Révoquer ce nœud ?',
      revokeNodeDescription:
        'Le nœud {{node}} et ses mandats seront révoqués. Il ne pourra plus recevoir de grant.',
      revokeMandateTitle: 'Révoquer ce mandat ?',
      revokeMandateDescription:
        'Le mandat {{capability}} entre {{source}} et {{target}} sera révoqué.',
      revoked: 'Révocation enregistrée.',
      revokeError: 'La révocation a échoué.',
      enrollmentNote:
        'Un nœud apparaît ici une fois lié : le control plane n’expose pas d’enrôlement en attente.',
    },
    steward: {
      title: 'Jetons mcp/steward de l’instance',
      description:
        'Jetons émis pour un mandat steward ; ils ne sont jamais affichés en clair après leur émission.',
      empty: 'Aucun jeton mcp/steward émis.',
      forbidden: 'Réservé à l’administrateur de l’instance.',
      disabled: 'La surface mcp/steward n’est pas activée sur cette instance.',
      error: 'Impossible de charger les jetons mcp/steward.',
      grant: 'Jeton',
      requester: 'Titulaire',
      tools: 'Outils',
      issuedBy: 'Remis à',
      createdAt: 'Émis le',
      expiresAt: 'Expire le',
      lastUsedAt: 'Dernier usage',
      revokeTitle: 'Révoquer ce jeton ?',
      revokeDescription:
        'Le jeton remis à {{holder}} cessera de fonctionner à son prochain appel.',
      revoked: 'Jeton révoqué.',
      revokeError: 'La révocation du jeton a échoué.',
    },
    mandates: {
      title: 'Mandats d’approbation',
      description:
        'Ce qu’une personne de l’organisation délègue à un Agent ou à un assistant : portée, politique des effets, limites et échéance. La signature reste celle du mandant ; cette page lit et révoque.',
      empty: 'Aucun mandat d’approbation dans cette organisation.',
      error: 'Impossible de charger les mandats d’approbation.',
      columns: { mandate: 'Mandat', principal: 'Mandant', holder: 'Détenteur', scope: 'Portée', expires: 'Expire le' },
      versionLabel: '{{id}} · v{{version}}',
      targetsCount_one: '{{count}} cible',
      targetsCount_other: '{{count}} cibles',
      states: { active: 'Actif', revoked: 'Révoqué', expired: 'Expiré', superseded: 'Remplacé' },
      holderKinds: { agent_credential: 'Agent', assistant_grant: 'Assistant', agent_job: 'Exécution d’Agent', human: 'Personne' },
      detailTitle: 'Détail du mandat',
      close: 'Fermer',
      approvedBy: 'Signé par',
      approvedAt: 'Signé le',
      applies: 'Applications consommées',
      appliesValue: '{{used}} sur {{max}}',
      appliesUnbounded: '{{used}} (sans plafond)',
      resourceKinds: 'Genres de ressources',
      targetsTitle: 'Cibles',
      targetKinds: { agent: 'Agent', application: 'Application' },
      effectPolicyTitle: 'Politique des effets',
      effects: { sandboxed: 'Bac à sable', published: 'Publié', external: 'Externe', governance: 'Gouvernance' },
      policies: { auto: 'automatique', confirm: 'confirmer', approve_distinct: 'approbation par une autre personne', forbidden: 'interdit' },
      revokedBy: 'Révoqué par {{name}} le {{date}}',
      revokedReason: 'Motif : {{reason}}',
      technical: 'Détail technique',
      notFound: 'Ce mandat n’existe plus dans la liste de l’organisation (remplacé ou supprimé).',
      revokeTitle: 'Révoquer ce mandat ?',
      revokeDescription_one:
        'Le mandat de {{principal}} pour {{holder}} ({{count}} cible) cessera d’admettre une exécution dès maintenant. La révocation est journalisée avec son motif.',
      revokeDescription_other:
        'Le mandat de {{principal}} pour {{holder}} ({{count}} cibles) cessera d’admettre une exécution dès maintenant. La révocation est journalisée avec son motif.',
      reason: 'Motif de la révocation',
      revoked: 'Mandat révoqué.',
      revokeError: 'La révocation du mandat a échoué.',
    },
    nodeMandates: {
      title: 'Mandats de nœud',
      description:
        'Les mandats que des membres ont signés entre deux de leurs nœuds pour une Application ou un Agent de l’organisation. Vous les lisez et pouvez révoquer un mandat ; la liaison du nœud, le poste personnel du membre, n’est jamais touchée. Les autres nœuds personnels restent invisibles.',
      empty: 'Aucun mandat de nœud ne vise cette organisation.',
      error: 'Impossible de charger les mandats de nœud.',
      disabled: 'Le contrôle des nœuds est désactivé sur cette instance : aucun mandat de nœud n’y est enregistré.',
      columns: { owner: 'Membre propriétaire', target: 'Application visée', nodes: 'Nœuds (source → cible)', issued: 'Signé le' },
      pair: '{{source}} → {{target}}',
      states: { active: 'Actif', revoked: 'Révoqué' },
      outsideMember: 'personne hors de l’organisation',
      noApplication: 'Agent de l’organisation',
      detailTitle: 'Détail du mandat de nœud',
      agent: 'Agent',
      sourceNode: 'Nœud source',
      targetNode: 'Nœud cible',
      capability: 'Capacité',
      capabilityKinds: { application_chat: 'Conversation d’Application', other: 'Autre capacité' },
      attachmentLabel: 'Rattachement',
      attachment: { stored: 'à la signature', derived: 'déduit à la lecture (mandat antérieur)' },
      bindingUntouched: 'Révoquer ce mandat ne délie pas le nœud : le poste du membre reste lié à son compte, seul ce mandat cesse d’ouvrir le canal vers l’organisation.',
      notFound: 'Ce mandat de nœud ne vise plus cette organisation ou n’existe plus.',
      revokeAction: 'Révoquer le mandat',
      revokeTitle: 'Révoquer ce mandat de nœud ?',
      revokeDescription: 'Le mandat de {{owner}} pour {{target}} cessera d’ouvrir le canal dès maintenant. La révocation est journalisée avec son motif et son auteur.',
      revoked: 'Mandat de nœud révoqué. La liaison du nœud est inchangée.',
      revokeError: 'La révocation du mandat de nœud a échoué.',
    },
    members: {
      title: 'Membres & annuaire',
      description:
        'Les personnes de l’organisation, leurs invitations et leurs rôles métier.',
      tabs: {
        members: 'Membres',
        invitations: 'Invitations',
        directory: 'Rôles métier',
      },
      person: 'Personne',
      email: 'E-mail',
      role: 'Rôle',
      joinedAt: 'Membre depuis',
      empty: 'Aucun membre.',
      error: 'Impossible de charger les membres.',
      changeRole: 'Changer le rôle de {{name}}',
      roleUpdated: 'Rôle mis à jour.',
      roleError: 'Le changement de rôle a échoué.',
      removeTitle: 'Retirer ce membre ?',
      removeDescription: '{{name}} perdra l’accès à l’organisation.',
      removed: 'Membre retiré.',
      removeError: 'Le retrait a échoué.',
      roles: {
        'super-admin': 'Super-administrateur',
        admin: 'Administrateur',
        'billing-admin': 'Administrateur de facturation',
        'orga-admin': 'Administrateur de l’organisation',
        member: 'Membre',
        guest: 'Invité',
        unknown: 'Rôle inconnu',
      },
    },
    invitations: {
      inviteLabel: 'Inviter par e-mail',
      invitePlaceholder: 'prenom.nom@exemple.fr',
      invite: 'Inviter',
      invited: 'Invitation envoyée.',
      inviteError: 'L’invitation a échoué.',
      emailInvalid: 'Saisissez une adresse e-mail valide.',
      empty: 'Aucune invitation en attente.',
      error: 'Impossible de charger les invitations.',
      expiresAt: 'Expire le',
      sentAt: 'Envoyée le',
      revokeTitle: 'Annuler cette invitation ?',
      revokeDescription: 'L’invitation de {{email}} ne pourra plus être acceptée.',
      revoked: 'Invitation annulée.',
      revokeError: 'L’annulation a échoué.',
      adminOnly: 'Seul un administrateur de l’organisation peut inviter.',
    },
    directory: {
      description:
        'Les rôles métier de l’annuaire décident des surfaces protégées par un rôle requis. Lecture seule ici.',
      roles: 'Rôles métier',
      team: 'Équipe',
      noRole: 'aucun rôle métier',
      empty: 'L’annuaire de l’organisation est vide.',
      forbidden: 'Seul un administrateur de l’organisation peut lire l’annuaire.',
      error: 'Impossible de charger l’annuaire.',
    },
    usage: {
      title: 'Usage & facturation',
      description:
        'La consommation LLM et MCP mesurée pour l’organisation, tous Agents confondus.',
      llmTitle: 'Modèles',
      mcpTitle: 'Outils MCP',
      provider: 'Provider',
      model: 'Modèle',
      server: 'Serveur',
      tool: 'Outil',
      calls: 'Appels',
      tokens: 'Tokens',
      errors: 'Erreurs',
      latency: 'Latence moyenne',
      cost: 'Coût',
      period: 'Période mesurée : du {{from}} au {{to}} (UTC).',
      truncated: 'Seules les {{count}} lignes journalières les plus récentes sont prises en compte.',
      llmEmpty: 'Aucun appel LLM mesuré.',
      mcpEmpty: 'Aucun appel MCP mesuré.',
      unavailable:
        'Statistiques d’usage indisponibles sur cette instance : le stockage de mesure ne répond pas.',
      credits: 'Crédits et facturation',
      creditsDescription: 'Les crédits, factures et moyens de paiement se gèrent dans la Console.',
      openConsole: 'Ouvrir la Console',
    },
    accessMap: {
      title: 'Droits',
      description: 'Qui peut appeler quoi, par quelle porte, et pourquoi.',
      freshness: 'Calculé il y a {{count}} s',
      stale: 'Projection périmée : actualisez',
      disabled: 'Cette instance n’expose pas encore la vue Droits.',
      error: 'La vue Droits est indisponible : le serveur n’a pas répondu.',
      empty: 'Aucune Application ne publie encore.',
      emptyFiltered: 'Aucune ligne ne correspond à ces filtres.',
      search: 'Rechercher',
      filterApplication: 'Filtrer par Application',
      allApplications: 'Toutes les Applications',
      filterState: 'Filtrer par état',
      allStates: 'Tous les états',
      filterVerdict: 'Filtrer par verdict',
      allVerdicts: 'Tous les verdicts',
      stateFilters: {
        member: 'Membre de l’organisation',
        not_member: 'Hors organisation',
        unresolved: 'Identité non résolue',
        published: 'Publié',
        not_published: 'Non publié',
        accessible: 'Accessible',
        inaccessible: 'Inaccessible',
        active: 'Actif',
        draft: 'Brouillon',
        expired: 'Expiré',
        revoked: 'Révoqué',
      },
      rowsCount: '{{count}} lignes',
      sortBy: 'Trier par',
      previous: 'Précédent',
      next: 'Suivant',
      page: 'Page {{page}} sur {{count}}',
      pivotLabel: 'Vue',
      betweenAgents: 'Entre Agents',
      pivots: { person: 'Par personne', resource: 'Par ressource', gate: 'Par porte' },
      columns: {
        requiredRole: 'Rôle requis',
        scope: 'Portée',
        expires: 'Expire le',
        used: 'Dernier usage',
        person: { name: 'Personne', role: 'Rôles', applications: 'Applications', callable: 'Ressources appelables', discussions: 'Discussions partagées', gates: 'Portes' },
        resource: { name: 'Ressource', application: 'Application', state: 'État', requiredRole: 'Rôle requis', callers: 'Appelants' },
        gate: { name: 'Porte', kind: 'Type', scope: 'Portée', expires: 'Expire le', used: 'Dernier usage', state: 'État' },
      },
      states: {
        registered: 'Enregistré',
        published: 'Publié',
        notPublished: 'Non publié',
        accessible: 'Accessible',
        inaccessible: 'Inaccessible',
        roleNotApplicable: 'non applicable',
      },
      resourceKinds: { workflow: 'Workflow', capability: 'Capacité', miniapp: 'MiniApp', tool: 'Outil accordé' },
      holderKinds: { person: 'Personne', api_key: 'Clé d’API', assistant: 'Assistant', agent: 'Agent' },
      gates: { session: 'Session', application_key: 'Clé d’API', assistant_token: 'Jeton d’assistant', workflow_call: 'Appel d’Agent' },
      gateKinds: {
        application: 'Application',
        application_key: 'Clé d’API',
        assistant_token: 'Jeton d’assistant',
        capability_grant: 'Appel entre Agents',
        approval_mandate: 'Mandat d’approbation',
        node_mandate: 'Mandat de nœud',
      },
      gateStates: { active: 'Actif', draft: 'Brouillon', expired: 'Expiré', revoked: 'Révoqué' },
      scopes: { application: 'Application', organization: 'Organisation', instance: 'Instance', references: 'Références' },
      audiences: { listed: 'Personnes listées', organization_members: 'Tous les membres de l’organisation' },
      homes: {
        application_access: 'Application › Accès',
        application_review: 'Application › Revue',
        directory: 'Annuaire',
        members: 'Membres & annuaire',
        gates: 'Portes machine',
        node_mandates: 'Portes machine › Mandats de nœud',
        agent_tools: 'Agent › Réglages',
        run_application: 'Ouvrir l’Application',
      },
      inspector: {
        title: 'Inspecteur',
        empty: 'Sélectionnez une ligne pour voir ce qu’elle peut appeler et pourquoi.',
        organizationRole: 'Rôle dans l’organisation : {{role}}',
        notMember: 'Hors organisation',
        businessRoles: 'Rôles métier',
        applications: 'Applications',
        none: 'aucun',
        callableTitle: 'Ressources appelables',
        callersTitle: 'Qui peut l’appeler',
        explainPair: 'Expliquer',
        openHome: 'Ouvrir la maison',
        revoke: 'Révoquer',
        revokeHint: 'La révocation se fait dans la maison de l’objet ; la vue Droits ne modifie rien.',
        nodeMandateNote: 'Un mandat de nœud ouvre le canal entre deux nœuds du membre ; le droit sur l’Application reste celui de la personne. Le simulateur explique donc la chaîne du membre propriétaire.',
        removePersonHint: 'Retirer une personne d’une Application se fait dans Application › Accès.',
        issuedAt: 'Émis le',
        issuedBy: 'Émis par',
        homeMissing: 'Cette porte n’a pas encore de page dans l’OS : la révocation passe par la Console ou la CLI.',
        noExpiry: 'sans échéance',
        audience: 'Qui entre',
        audienceListedHint: 'Seules les personnes listées entrent dans l’Application.',
        audienceOrganizationHint:
          'Tous les membres de l’organisation entrent, y compris les nouveaux : retirer quelqu’un de la liste ne lui retire pas l’accès tant qu’il est membre.',
        audienceChangeHint: 'L’audience se règle dans Application › Accès › Personnes.',
        sharedDiscussionsTitle: 'Discussions partagées',
        sharedDiscussionInvited: 'Invitation de {{name}}, le {{date}}',
        sharedDiscussionUnreachable: 'N’entre plus dans l’Application : l’invitation n’ouvre rien.',
        sharedDiscussionUntitled: 'Discussion sans titre',
      },
      simulator: {
        title: 'Simulateur',
        description: 'Aucun effet : le serveur évalue la question, rien n’est exécuté ; la simulation est notée comme lecture dans l’Activité.',
        holderKind: 'Type de porteur',
        holder: 'Porteur',
        resource: 'Ressource',
        gate: 'Porte',
        choose: 'Choisir…',
        submit: 'Évaluer',
        running: 'Évaluation…',
        error: 'L’évaluation a échoué.',
        unknownHolder: 'Ce porteur est inconnu dans cette organisation.',
      },
      chain: {
        title: 'Chaîne de décision',
        lastChanged: 'Dernière modification par {{by}}, {{at}}',
        lastChangedAt: 'Dernière modification : {{at}}',
        technical: 'Détail technique',
        copy: 'Copier le JSON',
        gesture: 'Geste : {{policy}}',
        noEffect: 'Simulation sans effet',
        gestures: { auto: 'automatique', confirm: 'confirmer', approve: 'approuver' },
        status: {
          pass: 'vérifié',
          fail: 'refusé',
          info: 'information',
          not_applicable: 'non applicable',
          not_evaluated: 'non évalué',
        },
        verdict: {
          allowed: 'Autorisé',
          out_of_scope: 'Refus : hors de portée',
          forbidden_role: 'Refus : rôle métier manquant',
          requires_approval: 'Approbation requise',
          not_published: 'Refus : non publié',
          token_expired: 'Refus : jeton expiré ou révoqué',
        },
        steps: {
          organization_member: 'Membre de l’organisation',
          application_person: 'Personne de l’Application',
          published: 'Publié sur la surface de l’Application',
          required_role: 'Rôle requis',
          gate_route: 'Porte compatible',
          application_key_binding: 'Clé rattachée à cette Application',
          application_key_active: 'Clé active',
          application_key_scope: 'Portée de la clé',
          assistant_gate: 'Porte des assistants',
          assistant_token_active: 'Jeton d’assistant actif',
          assistant_token_tool: 'Exécution permise au jeton',
          mandate_active: 'Mandat actif',
          mandate_scope: 'Portée du mandat',
          agent_spec_binding: 'Workflow appliqué par l’Agent Spec',
          application_rights: 'Droits actuels sur l’Application',
          gesture_policy: 'Politique du geste',
          mandate_effect: 'Effet autorisé par le mandat',
          input_validation: 'Validation de l’entrée',
          capability_exported: 'Capacité exportée',
          capability_grant: 'Appel permis à cet Agent',
          chat_entrypoint: 'Conversation de l’Application',
          tool_granted: 'Outil accordé et publié',
        },
        details: {
          not_member: 'n’est pas membre de l’organisation',
          not_listed: 'n’est pas parmi les personnes de l’Application',
          listed: 'parmi les personnes de l’Application',
          open_to_organization: 'Application ouverte à toute l’organisation',
          organization_members: 'membre de l’organisation, admis par l’audience « Tous les membres »',
          absent: 'absent de la surface',
          recorded_not_published: 'enregistré, pas encore publié',
          expired: 'expiré',
          revoked: 'révoqué',
          key_bound_to_another_application: 'clé d’une autre Application',
          capability_requires_application_key: 'une capacité s’appelle avec une clé d’API',
          agent_jobs_rail_not_configured: 'porte des assistants non configurée sur cette instance',
          granted_not_effective: 'accordé, mais absent de la surface publiée',
          chat_entrypoint_missing: 'aucune conversation configurée sur l’Application',
          chat_entrypoint_not_member: 'l’Agent de la conversation n’est pas membre de l’Application',
        },
      },
      absent: {
        title: 'Non projeté dans cette version',
        objects: {
          nodes: 'Nœuds liés',
          approval_mandates: 'Mandats d’approbation',
          node_mandates: 'Mandats de nœud',
          remote_instances: 'Instances distantes',
          activity: 'Activité (changements de droits, usages)',
          assistant_tokens: 'Jetons d’assistant',
          application_key_revocations: 'Révocations de clés d’API',
          application_history: 'Historique des personnes et surfaces',
          rights_changes_before_journal: 'Changements de droits antérieurs au journal',
        },
        reasons: {
          no_organization_route: 'aucune route d’organisation ne les liste',
          not_projected_in_lot_1: 'prévu dans un lot suivant',
          inter_instance_call_not_specified: 'l’appel entre instances n’est pas spécifié',
          agent_jobs_rail_not_configured: 'porte des assistants non configurée sur cette instance',
          owner_scoped: 'objets personnels : visibles dans Mes accès de leur titulaire',
          node_control_disabled: 'contrôle des nœuds désactivé sur cette instance',
          no_control_plane_route: 'le control plane n’expose pas encore vos autres instances',
          deleted_without_trace: 'une clé révoquée est supprimée sans trace',
          last_change_only: 'seule la dernière modification est conservée',
        },
      },
      journal: {
        title: 'Activité',
        error: 'L’Activité est indisponible : le serveur n’a pas répondu.',
        empty: 'Aucune activité pour ces filtres.',
        filterKind: 'Filtrer par type',
        allKinds: 'Tous les types',
        kinds: { change: 'Changement de droit', usage: 'Usage via une porte', simulation: 'Simulation' },
        readAccess: 'lecture',
        forRequester: 'pour {{name}}',
        systemActor: 'AgentMaurice (automatique)',
        detail: {
          person: 'Personne : {{name}}',
          surface: 'Version {{from}} → {{to}}',
          unpublished: 'non publiée',
          toolsAdded: 'accordés : {{tools}}',
          toolsRemoved: 'retirés : {{tools}}',
          toolsUnchanged: 'liste inchangée',
          key: 'Clé …{{suffix}}',
          peopleInitialized: '{{count}} personne(s) de l’organisation inscrite(s)',
          audience: 'Audience : {{from}} → {{to}}',
          discussion: 'Discussion « {{label}} » · {{name}}',
        },
        older: 'Plus anciens',
        latest: 'Plus récents',
        absentTitle: 'Non journalisé',
        columns: { at: 'Date', kind: 'Type', action: 'Action', object: 'Objet', actor: 'Acteur', verdict: 'Verdict ou état' },
        objects: {
          person: 'Personne',
          api_key: 'Clé d’API',
          assistant: 'Assistant',
          agent: 'Agent',
          application: 'Application',
          application_key: 'Clé d’API',
          assistant_token: 'Jeton d’assistant',
          approval_mandate: 'Mandat d’approbation',
          node_mandate: 'Mandat de nœud',
          run_session: 'Discussion Run',
        },
        actions: {
          access_map_explained: 'Simulation dans Droits',
          approval_mandate_signed: 'Mandat signé',
          approval_mandate_revoked: 'Mandat révoqué',
          node_mandate_revoked: 'Mandat de nœud révoqué (nœud toujours lié)',
          assistant_token_issued: 'Jeton d’assistant émis',
          assistant_token_revoked: 'Jeton d’assistant révoqué',
          application_key_issued: 'Clé d’API émise',
          application_key_revoked: 'Clé d’API révoquée',
          application_person_added: 'Personne ajoutée à l’Application',
          application_person_removed: 'Personne retirée de l’Application',
          application_people_initialized: 'Personnes de l’Application initialisées',
          application_surface_published: 'Surface publiée',
          application_tools_changed: 'Outils accordés modifiés',
          application_updated: 'Dernière modification de l’Application',
          application_audience_changed: 'Audience de l’Application modifiée',
          run_discussion_participant_added: 'Personne invitée dans une discussion',
          run_discussion_participant_removed: 'Personne retirée d’une discussion',
          job_admitted: 'Exécution admise',
        },
      },
    },
    vault: {
      title: 'Coffre & événements',
      description: 'Les grants du coffre et les abonnements MCP Events de l’organisation.',
      comingSoon:
        'Aucune route de l’instance ne liste encore les grants du coffre ni les abonnements MCP Events. Cette page les montrera dès qu’elles existeront.',
    },
    myAccess: {
      title: 'Mes accès',
      description: 'Vos assistants branchés, vos nœuds et ce que vous pouvez appeler dans les Applications où vous êtes membre.',
      refused: 'Connectez-vous pour voir vos accès.',
      disabled: 'Cette instance n’expose pas encore Mes accès.',
      error: 'Mes accès est indisponible : le serveur n’a pas répondu.',
      assistantsEmpty: 'Aucun assistant branché sur cette instance.',
      assistantToken: 'Jeton d’assistant',
      assistantDates: 'Émis le {{issued}} · expire le {{expires}} · dernier usage : {{used}}',
      nodesEmpty: 'Aucun nœud lié à votre compte.',
      nodeDates: 'Lié le {{registered}} · {{count}} mandat(s)',
      manageNodes: 'Gérer et révoquer mes nœuds',
      mandatesTitle: 'Mes mandats d’approbation',
      mandateRoles: { principal: 'Je délègue', holder: 'Je détiens' },
      mandateExpires: 'Expire le {{date}}',
      controlPlaneUnavailable: 'Instance courante seulement : le control plane n’expose pas encore les autres instances où votre compte est connu.',
      admitted: 'Membre',
      notAdmitted: 'Hors Application',
      entry: 'Mes accès',
      assistantsTitle: 'Mes assistants',
      nodesTitle: 'Mes nœuds',
      membershipsTitle: 'Où je suis membre',
      membershipsEmpty: 'Vous n’êtes membre d’aucune organisation de cette instance.',
      openApplication: 'Ouvrir',
      membershipNamed: 'Membre nominatif',
      membershipOpen: 'Ouverte à toute l’organisation',
    },
  },
  en: {
    common: {
      identityUnresolved: 'Unresolved identity',
      loading: 'Loading…',
      refresh: 'Refresh',
      revoke: 'Revoke',
      remove: 'Remove',
      cancel: 'Cancel',
      never: 'never',
      notProvided: 'not provided',
      forbidden: 'Your role does not allow you to view this page.',
      selectOrganization: 'Select an organization.',
      comingSoon: 'Coming soon',
      statusLabel: 'Status',
      actions: 'Actions',
      status: {
        active: 'Active',
        revoked: 'Revoked',
        expired: 'Expired',
        pending: 'Pending',
        unknown: 'Unknown state',
      },
    },
    gates: {
      title: 'Machine gates',
      description:
        'Access without a human session: coding agents, connected assistants, nodes, approval mandates and node mandates that target the organization.',
      tabs: {
        codeAgents: 'Coding agents',
        assistants: 'Assistants',
        nodes: 'Nodes',
        mandates: 'Approval mandates',
        nodeMandates: 'Node mandates',
      },
    },
    assistants: {
      title: 'Connected assistants',
      onOne:
        'Connecting an assistant (MCP URL and token) is available on One. In the OS, it ships with lot 2 of the MCP front door.',
      noList: 'No instance route lists assistant tokens outside the local device yet.',
    },
    nodes: {
      ownerScope:
        'The control plane only lists the nodes bound to your account: it does not know other members’ nodes.',
      nodesTitle: 'Bound nodes',
      nodesEmpty: 'No node bound to your account.',
      mandatesTitle: 'Node mandates',
      mandatesEmpty: 'No node mandate.',
      node: 'Node',
      algorithm: 'Algorithm',
      pair: 'Pair',
      capability: 'Capability',
      kind: 'Kind',
      issuedAt: 'Issued',
      revokedAt: 'Revoked',
      hankoRequired:
        'Requires a Hanko session: the node control plane does not accept a local session.',
      disabled: 'The node control plane is not enabled on this instance.',
      error: 'Could not load nodes.',
      revokeNodeTitle: 'Revoke this node?',
      revokeNodeDescription:
        'Node {{node}} and its mandates will be revoked. It will no longer receive grants.',
      revokeMandateTitle: 'Revoke this mandate?',
      revokeMandateDescription:
        'Mandate {{capability}} between {{source}} and {{target}} will be revoked.',
      revoked: 'Revocation recorded.',
      revokeError: 'Revocation failed.',
      enrollmentNote:
        'A node shows up here once bound: the control plane exposes no pending enrollment.',
    },
    steward: {
      title: 'Instance mcp/steward tokens',
      description:
        'Tokens issued for a steward mandate; they are never shown in clear after issuance.',
      empty: 'No mcp/steward token issued.',
      forbidden: 'Restricted to the instance administrator.',
      disabled: 'The mcp/steward surface is not enabled on this instance.',
      error: 'Could not load mcp/steward tokens.',
      grant: 'Token',
      requester: 'Holder',
      tools: 'Tools',
      issuedBy: 'Handed to',
      createdAt: 'Issued',
      expiresAt: 'Expires',
      lastUsedAt: 'Last used',
      revokeTitle: 'Revoke this token?',
      revokeDescription: 'The token handed to {{holder}} will stop working on its next call.',
      revoked: 'Token revoked.',
      revokeError: 'Token revocation failed.',
    },
    mandates: {
      title: 'Approval mandates',
      description:
        'What a person of the organization delegates to an Agent or an assistant: scope, effect policy, limits and deadline. Signing stays the principal’s act; this page reads and revokes.',
      empty: 'No approval mandate in this organization.',
      error: 'Approval mandates could not be loaded.',
      columns: { mandate: 'Mandate', principal: 'Principal', holder: 'Holder', scope: 'Scope', expires: 'Expires' },
      versionLabel: '{{id}} · v{{version}}',
      targetsCount_one: '{{count}} target',
      targetsCount_other: '{{count}} targets',
      states: { active: 'Active', revoked: 'Revoked', expired: 'Expired', superseded: 'Superseded' },
      holderKinds: { agent_credential: 'Agent', assistant_grant: 'Assistant', agent_job: 'Agent run', human: 'Person' },
      detailTitle: 'Mandate detail',
      close: 'Close',
      approvedBy: 'Signed by',
      approvedAt: 'Signed on',
      applies: 'Applies used',
      appliesValue: '{{used}} of {{max}}',
      appliesUnbounded: '{{used}} (no cap)',
      resourceKinds: 'Resource kinds',
      targetsTitle: 'Targets',
      targetKinds: { agent: 'Agent', application: 'Application' },
      effectPolicyTitle: 'Effect policy',
      effects: { sandboxed: 'Sandboxed', published: 'Published', external: 'External', governance: 'Governance' },
      policies: { auto: 'automatic', confirm: 'confirm', approve_distinct: 'approval by another person', forbidden: 'forbidden' },
      revokedBy: 'Revoked by {{name}} on {{date}}',
      revokedReason: 'Reason: {{reason}}',
      technical: 'Technical detail',
      notFound: 'This mandate is no longer in the organization’s list (superseded or removed).',
      revokeTitle: 'Revoke this mandate?',
      revokeDescription_one:
        '{{principal}}’s mandate for {{holder}} ({{count}} target) stops admitting runs now. The revocation is journaled with its reason.',
      revokeDescription_other:
        '{{principal}}’s mandate for {{holder}} ({{count}} targets) stops admitting runs now. The revocation is journaled with its reason.',
      reason: 'Reason for the revocation',
      revoked: 'Mandate revoked.',
      revokeError: 'The mandate could not be revoked.',
    },
    nodeMandates: {
      title: 'Node mandates',
      description:
        'Mandates that members signed between two of their nodes for an Application or an Agent of the organization. You read them and may revoke a mandate; the node binding, the member’s personal device, is never touched. Other personal nodes stay invisible.',
      empty: 'No node mandate targets this organization.',
      error: 'Node mandates could not be loaded.',
      disabled: 'Node control is disabled on this instance: no node mandate is recorded here.',
      columns: { owner: 'Owning member', target: 'Targeted Application', nodes: 'Nodes (source → target)', issued: 'Signed on' },
      pair: '{{source}} → {{target}}',
      states: { active: 'Active', revoked: 'Revoked' },
      outsideMember: 'person outside the organization',
      noApplication: 'Agent of the organization',
      detailTitle: 'Node mandate detail',
      agent: 'Agent',
      sourceNode: 'Source node',
      targetNode: 'Target node',
      capability: 'Capability',
      capabilityKinds: { application_chat: 'Application conversation', other: 'Other capability' },
      attachmentLabel: 'Attachment',
      attachment: { stored: 'when signed', derived: 'derived when read (earlier mandate)' },
      bindingUntouched: 'Revoking this mandate does not unlink the node: the member’s device stays linked to their account; only this mandate stops opening the channel to the organization.',
      notFound: 'This node mandate no longer targets this organization or no longer exists.',
      revokeAction: 'Revoke the mandate',
      revokeTitle: 'Revoke this node mandate?',
      revokeDescription: 'The mandate of {{owner}} for {{target}} stops opening the channel now. The revocation is journaled with its reason and author.',
      revoked: 'Node mandate revoked. The node binding is unchanged.',
      revokeError: 'The node mandate could not be revoked.',
    },
    members: {
      title: 'Members & directory',
      description: 'The organization’s people, their invitations and their business roles.',
      tabs: {
        members: 'Members',
        invitations: 'Invitations',
        directory: 'Business roles',
      },
      person: 'Person',
      email: 'Email',
      role: 'Role',
      joinedAt: 'Member since',
      empty: 'No member.',
      error: 'Could not load members.',
      changeRole: 'Change the role of {{name}}',
      roleUpdated: 'Role updated.',
      roleError: 'Role change failed.',
      removeTitle: 'Remove this member?',
      removeDescription: '{{name}} will lose access to the organization.',
      removed: 'Member removed.',
      removeError: 'Removal failed.',
      roles: {
        'super-admin': 'Super administrator',
        admin: 'Administrator',
        'billing-admin': 'Billing administrator',
        'orga-admin': 'Organization administrator',
        member: 'Member',
        guest: 'Guest',
        unknown: 'Unknown role',
      },
    },
    invitations: {
      inviteLabel: 'Invite by email',
      invitePlaceholder: 'first.last@example.com',
      invite: 'Invite',
      invited: 'Invitation sent.',
      inviteError: 'Invitation failed.',
      emailInvalid: 'Enter a valid email address.',
      empty: 'No pending invitation.',
      error: 'Could not load invitations.',
      expiresAt: 'Expires',
      sentAt: 'Sent',
      revokeTitle: 'Cancel this invitation?',
      revokeDescription: 'The invitation for {{email}} can no longer be accepted.',
      revoked: 'Invitation cancelled.',
      revokeError: 'Cancellation failed.',
      adminOnly: 'Only an organization administrator can invite.',
    },
    directory: {
      description:
        'Directory business roles decide which surfaces a required role protects. Read-only here.',
      roles: 'Business roles',
      team: 'Team',
      noRole: 'no business role',
      empty: 'The organization directory is empty.',
      forbidden: 'Only an organization administrator can read the directory.',
      error: 'Could not load the directory.',
    },
    usage: {
      title: 'Usage & billing',
      description:
        'The LLM and MCP consumption metered for the organization, across all Agents.',
      llmTitle: 'Models',
      mcpTitle: 'MCP tools',
      provider: 'Provider',
      model: 'Model',
      server: 'Server',
      tool: 'Tool',
      calls: 'Calls',
      tokens: 'Tokens',
      errors: 'Errors',
      latency: 'Average latency',
      cost: 'Cost',
      period: 'Metered period: {{from}} to {{to}} (UTC).',
      truncated: 'Only the {{count}} most recent daily rows are counted.',
      llmEmpty: 'No LLM call metered.',
      mcpEmpty: 'No MCP call metered.',
      unavailable: 'Usage statistics are unavailable on this instance: the metering store does not answer.',
      credits: 'Credits and billing',
      creditsDescription: 'Credits, invoices and payment methods are managed in the Console.',
      openConsole: 'Open the Console',
    },
    accessMap: {
      title: 'Access rights',
      description: 'Who can call what, through which gate, and why.',
      freshness: 'Computed {{count}} s ago',
      stale: 'Stale projection: refresh',
      disabled: 'This instance does not expose the Access rights view yet.',
      error: 'The Access rights view is unavailable: the server did not answer.',
      empty: 'No Application publishes anything yet.',
      emptyFiltered: 'No row matches these filters.',
      search: 'Search',
      filterApplication: 'Filter by Application',
      allApplications: 'All Applications',
      filterState: 'Filter by state',
      allStates: 'All states',
      filterVerdict: 'Filter by verdict',
      allVerdicts: 'All verdicts',
      stateFilters: {
        member: 'Member of the organization',
        not_member: 'Outside the organization',
        unresolved: 'Unresolved identity',
        published: 'Published',
        not_published: 'Not published',
        accessible: 'Accessible',
        inaccessible: 'Inaccessible',
        active: 'Active',
        draft: 'Draft',
        expired: 'Expired',
        revoked: 'Revoked',
      },
      rowsCount: '{{count}} rows',
      sortBy: 'Sort by',
      previous: 'Previous',
      next: 'Next',
      page: 'Page {{page}} of {{count}}',
      pivotLabel: 'View',
      betweenAgents: 'Between Agents',
      pivots: { person: 'By person', resource: 'By resource', gate: 'By gate' },
      columns: {
        requiredRole: 'Required role',
        scope: 'Scope',
        expires: 'Expires',
        used: 'Last use',
        person: { name: 'Person', role: 'Roles', applications: 'Applications', callable: 'Callable resources', discussions: 'Shared discussions', gates: 'Gates' },
        resource: { name: 'Resource', application: 'Application', state: 'State', requiredRole: 'Required role', callers: 'Callers' },
        gate: { name: 'Gate', kind: 'Type', scope: 'Scope', expires: 'Expires', used: 'Last use', state: 'State' },
      },
      states: {
        registered: 'Recorded',
        published: 'Published',
        notPublished: 'Not published',
        accessible: 'Accessible',
        inaccessible: 'Not accessible',
        roleNotApplicable: 'not applicable',
      },
      resourceKinds: { workflow: 'Workflow', capability: 'Capability', miniapp: 'MiniApp', tool: 'Granted tool' },
      holderKinds: { person: 'Person', api_key: 'API key', assistant: 'Assistant', agent: 'Agent' },
      gates: { session: 'Session', application_key: 'API key', assistant_token: 'Assistant token', workflow_call: 'Agent call' },
      gateKinds: {
        application: 'Application',
        application_key: 'API key',
        assistant_token: 'Assistant token',
        capability_grant: 'Call between Agents',
        approval_mandate: 'Approval mandate',
        node_mandate: 'Node mandate',
      },
      gateStates: { active: 'Active', draft: 'Draft', expired: 'Expired', revoked: 'Revoked' },
      scopes: { application: 'Application', organization: 'Organization', instance: 'Instance', references: 'References' },
      audiences: { listed: 'Listed people', organization_members: 'All members of the organization' },
      homes: {
        application_access: 'Application › Access',
        application_review: 'Application › Review',
        directory: 'Directory',
        members: 'Members & directory',
        gates: 'Machine gates',
        node_mandates: 'Machine gates › Node mandates',
        agent_tools: 'Agent › Settings',
        run_application: 'Open the Application',
      },
      inspector: {
        title: 'Inspector',
        empty: 'Select a row to see what it can call and why.',
        organizationRole: 'Organization role: {{role}}',
        notMember: 'Outside the organization',
        businessRoles: 'Business roles',
        applications: 'Applications',
        none: 'none',
        callableTitle: 'Callable resources',
        callersTitle: 'Who can call it',
        explainPair: 'Explain',
        openHome: 'Open its home',
        revoke: 'Revoke',
        revokeHint: 'Revocation happens in the object’s home; the Access rights view changes nothing.',
        nodeMandateNote: 'A node mandate opens the channel between two nodes of the member; the right on the Application stays the person’s. The simulator therefore explains the owning member’s chain.',
        removePersonHint: 'Removing a person from an Application happens in Application › Access.',
        issuedAt: 'Issued',
        issuedBy: 'Issued by',
        homeMissing: 'This gate has no OS page yet: revoke it from the Console or the CLI.',
        noExpiry: 'no expiry',
        audience: 'Who enters',
        audienceListedHint: 'Only the listed people enter the Application.',
        audienceOrganizationHint:
          'All members of the organization enter, new ones included: removing someone from the list does not remove their access while they are a member.',
        audienceChangeHint: 'The audience is set in Application › Access › People.',
        sharedDiscussionsTitle: 'Shared discussions',
        sharedDiscussionInvited: 'Invited by {{name}} on {{date}}',
        sharedDiscussionUnreachable: 'No longer enters the Application: the invitation opens nothing.',
        sharedDiscussionUntitled: 'Untitled discussion',
      },
      simulator: {
        title: 'Simulator',
        description: 'No effect: the server evaluates the question, nothing runs; the simulation is recorded as a read in Activity.',
        holderKind: 'Holder type',
        holder: 'Holder',
        resource: 'Resource',
        gate: 'Gate',
        choose: 'Choose…',
        submit: 'Evaluate',
        running: 'Evaluating…',
        error: 'The evaluation failed.',
        unknownHolder: 'This holder is unknown in this organization.',
      },
      chain: {
        title: 'Decision chain',
        lastChanged: 'Last changed by {{by}}, {{at}}',
        lastChangedAt: 'Last changed: {{at}}',
        technical: 'Technical detail',
        copy: 'Copy JSON',
        gesture: 'Gesture: {{policy}}',
        noEffect: 'Side-effect-free simulation',
        gestures: { auto: 'automatic', confirm: 'confirm', approve: 'approve' },
        status: {
          pass: 'checked',
          fail: 'refused',
          info: 'information',
          not_applicable: 'not applicable',
          not_evaluated: 'not evaluated',
        },
        verdict: {
          allowed: 'Allowed',
          out_of_scope: 'Refused: out of scope',
          forbidden_role: 'Refused: missing business role',
          requires_approval: 'Approval required',
          not_published: 'Refused: not published',
          token_expired: 'Refused: token expired or revoked',
        },
        steps: {
          organization_member: 'Member of the organization',
          application_person: 'Person of the Application',
          published: 'Published on the Application surface',
          required_role: 'Required role',
          gate_route: 'Compatible gate',
          application_key_binding: 'Key bound to this Application',
          application_key_active: 'Active key',
          application_key_scope: 'Key scope',
          assistant_gate: 'Assistant gate',
          assistant_token_active: 'Active assistant token',
          assistant_token_tool: 'Execution allowed to the token',
          mandate_active: 'Active mandate',
          mandate_scope: 'Mandate scope',
          agent_spec_binding: 'Workflow applied by the Agent Spec',
          application_rights: 'Current rights on the Application',
          gesture_policy: 'Gesture policy',
          mandate_effect: 'Effect allowed by the mandate',
          input_validation: 'Input validation',
          capability_exported: 'Exported capability',
          capability_grant: 'Call allowed to this Agent',
          chat_entrypoint: 'Application conversation',
          tool_granted: 'Tool granted and published',
        },
        details: {
          not_member: 'is not a member of the organization',
          not_listed: 'is not among the people of the Application',
          listed: 'among the people of the Application',
          open_to_organization: 'Application open to the whole organization',
          organization_members: 'member of the organization, admitted by the “All members” audience',
          absent: 'absent from the surface',
          recorded_not_published: 'recorded, not published yet',
          expired: 'expired',
          revoked: 'revoked',
          key_bound_to_another_application: 'key of another Application',
          capability_requires_application_key: 'a capability is called with an API key',
          agent_jobs_rail_not_configured: 'assistant gate not configured on this instance',
          granted_not_effective: 'granted, but absent from the published surface',
          chat_entrypoint_missing: 'no conversation configured on the Application',
          chat_entrypoint_not_member: 'the conversation Agent is not a member of the Application',
        },
      },
      absent: {
        title: 'Not projected in this version',
        objects: {
          nodes: 'Linked nodes',
          approval_mandates: 'Approval mandates',
          node_mandates: 'Node mandates',
          remote_instances: 'Remote instances',
          activity: 'Activity (rights changes, uses)',
          assistant_tokens: 'Assistant tokens',
          application_key_revocations: 'API key revocations',
          application_history: 'History of people and surfaces',
          rights_changes_before_journal: 'Changes of rights made before the journal',
        },
        reasons: {
          no_organization_route: 'no organization route lists them',
          not_projected_in_lot_1: 'planned in a later lot',
          inter_instance_call_not_specified: 'the call between instances is not specified',
          agent_jobs_rail_not_configured: 'assistant gate not configured on this instance',
          owner_scoped: 'personal objects: visible in their holder’s My access',
          node_control_disabled: 'node control disabled on this instance',
          no_control_plane_route: 'the control plane does not expose your other instances yet',
          deleted_without_trace: 'a revoked key is deleted without trace',
          last_change_only: 'only the last change is kept',
        },
      },
      journal: {
        title: 'Activity',
        error: 'Activity is unavailable: the server did not answer.',
        empty: 'No activity for these filters.',
        filterKind: 'Filter by type',
        allKinds: 'All types',
        kinds: { change: 'Rights change', usage: 'Use through a gate', simulation: 'Simulation' },
        readAccess: 'read',
        forRequester: 'for {{name}}',
        systemActor: 'AgentMaurice (automatic)',
        detail: {
          person: 'Person: {{name}}',
          surface: 'Version {{from}} → {{to}}',
          unpublished: 'unpublished',
          toolsAdded: 'granted: {{tools}}',
          toolsRemoved: 'removed: {{tools}}',
          toolsUnchanged: 'list unchanged',
          key: 'Key …{{suffix}}',
          peopleInitialized: '{{count}} organization person(s) listed',
          audience: 'Audience: {{from}} → {{to}}',
          discussion: 'Discussion “{{label}}” · {{name}}',
        },
        older: 'Older',
        latest: 'Latest',
        absentTitle: 'Not journaled',
        columns: { at: 'Date', kind: 'Type', action: 'Action', object: 'Object', actor: 'Actor', verdict: 'Verdict or state' },
        objects: {
          person: 'Person',
          api_key: 'API key',
          assistant: 'Assistant',
          agent: 'Agent',
          application: 'Application',
          application_key: 'API key',
          assistant_token: 'Assistant token',
          approval_mandate: 'Approval mandate',
          node_mandate: 'Node mandate',
          run_session: 'Run discussion',
        },
        actions: {
          access_map_explained: 'Simulation in Access rights',
          approval_mandate_signed: 'Mandate signed',
          approval_mandate_revoked: 'Mandate revoked',
          node_mandate_revoked: 'Node mandate revoked (node still linked)',
          assistant_token_issued: 'Assistant token issued',
          assistant_token_revoked: 'Assistant token revoked',
          application_key_issued: 'API key issued',
          application_key_revoked: 'API key revoked',
          application_person_added: 'Person added to the Application',
          application_person_removed: 'Person removed from the Application',
          application_people_initialized: 'Application people initialized',
          application_surface_published: 'Surface published',
          application_tools_changed: 'Granted tools changed',
          application_updated: 'Last change of the Application',
          application_audience_changed: 'Application audience changed',
          run_discussion_participant_added: 'Person invited into a discussion',
          run_discussion_participant_removed: 'Person removed from a discussion',
          job_admitted: 'Execution admitted',
        },
      },
    },
    vault: {
      title: 'Vault & events',
      description: 'The organization’s vault grants and MCP Events subscriptions.',
      comingSoon:
        'No instance route lists vault grants or MCP Events subscriptions yet. This page will show them as soon as they exist.',
    },
    myAccess: {
      title: 'My access',
      description: 'Your connected assistants, your nodes and what you can call in the Applications you are a member of.',
      refused: 'Sign in to see your access.',
      disabled: 'This instance does not expose My access yet.',
      error: 'My access is unavailable: the server did not answer.',
      assistantsEmpty: 'No assistant connected on this instance.',
      assistantToken: 'Assistant token',
      assistantDates: 'Issued {{issued}} · expires {{expires}} · last use: {{used}}',
      nodesEmpty: 'No node linked to your account.',
      nodeDates: 'Linked {{registered}} · {{count}} mandate(s)',
      manageNodes: 'Manage and revoke my nodes',
      mandatesTitle: 'My approval mandates',
      mandateRoles: { principal: 'I delegate', holder: 'I hold' },
      mandateExpires: 'Expires {{date}}',
      controlPlaneUnavailable: 'Current instance only: the control plane does not expose the other instances where your account is known yet.',
      admitted: 'Member',
      notAdmitted: 'Outside the Application',
      entry: 'My access',
      assistantsTitle: 'My assistants',
      nodesTitle: 'My nodes',
      membershipsTitle: 'Where I am a member',
      membershipsEmpty: 'You are not a member of any organization on this instance.',
      openApplication: 'Open',
      membershipNamed: 'Named member',
      membershipOpen: 'Open to the whole organization',
    },
  },
}
